/**
 * Prioritization Session Types (v2.2)
 * 
 * A Session is the canonical unit of work—a configurable execution container
 * where prioritization work occurs. Sessions are not meetings. They are
 * structured work units.
 * 
 * Sessions:
 * 1. Declare exactly one intent and may carry legacy detailed decision-step identifiers
 * 2. Operate over a locked Item Pool and locked Criteria Set
 * 3. Produce Session Results (immutable outputs)
 * 4. May occur multiple times within a Loop
 * 
 * ============================================================================
 * LEGACY DETAILED DECISION-STEP IDS (S1-S6 shorthand)
 * ============================================================================
 * 
 * These identifiers are retained for compatibility with older orchestration code.
 * They are not the preferred canon vocabulary for decision class.
 *
 * They define a detailed subject-specific decision step:
 * - S1: Participation Prioritization - Who participates and in what roles
 * - S2: Item Pool Selection - Which items are in scope for ranking
 * - S3: Attribute Selection - Which attributes become criteria
 * - S4: Values Choice Selection - How value scales are defined
 * - S5: Value Selection - What values are assigned to items
 * - S6: Item Prioritization - Final force-ranking (requires all prerequisites)
 * 
 * Prerequisites are conditions, not a mandated order. S6 requires:
 * - participation locked (S1)
 * - pool locked (S2)
 * - criteria locked (S3)
 * - values sufficient (S5)
 * - configuration locked
 * 
 * ============================================================================
 * STATE MACHINE INVARIANTS
 * ============================================================================
 * 
 * INVARIANT 1: Sessions own execution; tasks never substitute.
 *   - The actual prioritization work happens in the Session
 *   - Session state machine: DRAFT → CRITERIA_FINALIZED → RUN_COMPLETE → PUBLISHED
 *   - Tasks are responsibility assignments pointing to Sessions
 * 
 * INVARIANT 2: A session can exist without a task, but not vice-versa.
 *   - Sessions are the canonical execution containers
 *   - Tasks are optional pointers for participant assignment
 *   - Every Task MUST reference exactly one Session
 * 
 * INVARIANT 3: Sessions do not advance phases.
 *   - Session state (DRAFT, PUBLISHED, etc.) is independent
 *   - Session completion contributes to prerequisite satisfaction
 *   - Prerequisites gate S6; phases are deprecated
 */

import type { ValueProviderConfig } from './cycleConfigSession';
import type { OutcomeArtifactMetadata } from './decisionInstance';
import type { AltitudeLevel } from './itemLifecycle';
import type { CandidateLanguageType } from './lifecycle';
import type { NestedPrioritizationEvent } from './nestedPrioritizationEvent';
import type { SituationVector } from './situationVector';

// ============================================================================
// Legacy Detailed Decision-Step IDs (S1-S6 shorthand)
// ============================================================================

/**
 * Legacy detailed decision-step identifier retained for compatibility.
 * Do not confuse this with the canonical decision-class kind:
 * selection | range_selection | convergence | force_ranking | review.
 */
export type DecisionClass = 
  | 'S1_PARTICIPATION_PRIORITIZATION'
  | 'S2_ITEM_POOL_SELECTION'
  | 'S3_ATTRIBUTE_SELECTION'
  | 'S4_VALUES_CHOICE_SELECTION'
  | 'S5_VALUE_SELECTION'
  | 'S6_ITEM_PRIORITIZATION';

/**
 * User-facing labels for Decision Classes
 */
export const DECISION_CLASS_LABELS: Record<DecisionClass, string> = {
  S1_PARTICIPATION_PRIORITIZATION: 'Participation Prioritization',
  S2_ITEM_POOL_SELECTION: 'Item Pool Selection',
  S3_ATTRIBUTE_SELECTION: 'Attribute Selection',
  S4_VALUES_CHOICE_SELECTION: 'Values Choice Selection',
  S5_VALUE_SELECTION: 'Value Selection',
  S6_ITEM_PRIORITIZATION: 'Item Prioritization',
};

/**
 * Short labels for Decision Classes
 */
export const DECISION_CLASS_SHORT_LABELS: Record<DecisionClass, string> = {
  S1_PARTICIPATION_PRIORITIZATION: 'Participation',
  S2_ITEM_POOL_SELECTION: 'Pool Selection',
  S3_ATTRIBUTE_SELECTION: 'Criteria',
  S4_VALUES_CHOICE_SELECTION: 'Scales',
  S5_VALUE_SELECTION: 'Values',
  S6_ITEM_PRIORITIZATION: 'Prioritization',
};

/**
 * Decision Class descriptions
 */
export const DECISION_CLASS_DESCRIPTIONS: Record<DecisionClass, string> = {
  S1_PARTICIPATION_PRIORITIZATION: 'Define who participates and in what roles for the cycle',
  S2_ITEM_POOL_SELECTION: 'Select which items are in scope for ranking',
  S3_ATTRIBUTE_SELECTION: 'Select which attributes become criteria for evaluation',
  S4_VALUES_CHOICE_SELECTION: 'Define value scales and choice rules for criteria',
  S5_VALUE_SELECTION: 'Assign values to items for selected criteria',
  S6_ITEM_PRIORITIZATION: 'Force-rank items to produce a published prioritization artifact with visible lineage',
};

/**
 * Force-ranking prerequisites expressed using the older S6 shorthand.
 */
export const S6_PREREQUISITES = [
  'PARTICIPATION_LOCKED',  // S1 complete
  'POOL_LOCKED',           // S2 complete
  'CRITERIA_LOCKED',       // S3 complete
  'VALUES_SUFFICIENT',     // S5 has required coverage
  'CONFIGURATION_LOCKED',  // Session configuration frozen
] as const;

export type S6Prerequisite = typeof S6_PREREQUISITES[number];

// ============================================================================
// Session Type (Legacy - maps to Decision Class)
// ============================================================================

/**
 * @deprecated Use DecisionClass instead.
 * Legacy session type for backward compatibility.
 * 
 * - engage: Maps to S1, S3, S4
 * - gather: Maps to S2, S5
 * - arrange: Maps to S6
 */
export type SessionType = 'engage' | 'gather' | 'arrange';

/**
 * User-facing labels for session types
 */
export const SESSION_TYPE_LABELS: Record<SessionType, string> = {
  engage: 'Setup Session',
  gather: 'Structuring Session',
  arrange: 'Ranking Session',
};

/**
 * Session type descriptions for UI
 */
export const SESSION_TYPE_DESCRIPTIONS: Record<SessionType, string> = {
  engage: 'Configure criteria, hierarchy, and participants for prioritization',
  gather: 'Collect, structure, and prepare items for evaluation',
  arrange: 'Apply ranking tools and scoring to derive priorities',
};

/**
 * Map legacy SessionType to valid Decision Classes
 */
export const SESSION_TYPE_TO_DECISION_CLASSES: Record<SessionType, DecisionClass[]> = {
  engage: ['S1_PARTICIPATION_PRIORITIZATION', 'S3_ATTRIBUTE_SELECTION', 'S4_VALUES_CHOICE_SELECTION'],
  gather: ['S2_ITEM_POOL_SELECTION', 'S5_VALUE_SELECTION'],
  arrange: ['S6_ITEM_PRIORITIZATION'],
};

// ============================================================================
// Comparison Cell Filter (for Altitude x Candidate Type filtering)
// ============================================================================

/**
 * A single cell in the 5x6 altitude/itemCategory grid.
 * Used to filter items for session eligibility.
 */
export interface GridCellFilter {
  altitudeLevel: AltitudeLevel;
  itemCategory: CandidateLanguageType;
}

// ============================================================================
// Session State
// ============================================================================

/**
 * Session state machine
 * 
 * DRAFT - Session created, configuring
 * CRITERIA_FINALIZED - Criteria set locked, adding candidates
 * RUN_COMPLETE - Tool has been run, outputs generated
 * PUBLISHED - Results published and shared
 */
export type SessionState = 
  | 'DRAFT' 
  | 'CRITERIA_FINALIZED' 
  | 'RUN_COMPLETE' 
  | 'PUBLISHED';

// ============================================================================
// Session Intent
// ============================================================================

/**
 * Session intent - declares the purpose of this session.
 * Required for gate validation in EGAp iterations.
 * 
 * - select_attributes: Prioritize attributes to decide which criteria matter
 * - converge_values: Produce consensus values for attributes on items
 * - select_item_pool: Decide which items make the cut for ranking
 * - rank_priorities: Force-rank the selected candidate pool
 * - calibration: Calibration session for alignment
 * - review: Structured comparative review without ranking
 */
export type SessionIntent = 
  | 'select_attributes'
  | 'converge_values'
  | 'select_item_pool'
  | 'rank_priorities'
  | 'calibration'
  | 'review';

/**
 * User-facing labels for session intents
 */
export const SESSION_INTENT_LABELS: Record<SessionIntent, string> = {
  select_attributes: 'Criteria Selection',
  converge_values: 'Value Convergence',
  select_item_pool: 'Item Pool Selection',
  rank_priorities: 'Priority Ranking',
  calibration: 'Calibration',
  review: 'Review',
};

/**
 * Session intent descriptions for UI
 */
export const SESSION_INTENT_DESCRIPTIONS: Record<SessionIntent, string> = {
  select_attributes: 'Select and prioritize which criteria/attributes matter for this prioritization',
  converge_values: 'Collect and reconcile attribute values from multiple sources',
  select_item_pool: 'Decide which items from the gathered set will be ranked',
  rank_priorities: 'Apply ranking tools to produce a priority ordering',
  calibration: 'Align participants on scoring standards and interpretation',
  review: 'Structured review and comparison of items without producing a ranking',
};

/**
 * Canonical Session Type - what the session is about.
 * This is the primary customer-facing subject of judgment.
 */
export type CanonicalSessionType =
  | 'participants'
  | 'item_pool'
  | 'criteria'
  | 'scales'
  | 'values'
  | 'items';

export const CANONICAL_SESSION_TYPE_LABELS: Record<CanonicalSessionType, string> = {
  participants: 'Participants',
  item_pool: 'Item pool',
  criteria: 'Criteria',
  scales: 'Scales',
  values: 'Values',
  items: 'Items',
};

/**
 * Canonical Decision Class - what kind of judgment is being made.
 */
export type CanonicalDecisionClassKind =
  | 'selection'
  | 'range_selection'
  | 'convergence'
  | 'force_ranking'
  | 'review';

export const CANONICAL_DECISION_CLASS_LABELS: Record<CanonicalDecisionClassKind, string> = {
  selection: 'Selection',
  range_selection: 'Range selection',
  convergence: 'Convergence',
  force_ranking: 'Force ranking',
  review: 'Review',
};

export const CANONICAL_DECISION_CLASS_DESCRIPTIONS: Record<CanonicalDecisionClassKind, string> = {
  selection: 'Choose a bounded set from candidates in scope.',
  range_selection: 'Choose ranges, bands, thresholds, or cut lines.',
  convergence: 'Resolve uncertain or disputed values into a usable answer.',
  force_ranking: 'Produce a strict total ordering.',
  review: 'Compare or calibrate without creating a strict total ordering.',
};

/**
 * Which legacy session types are valid for each intent
 */
export const INTENT_SESSION_TYPE_MAP: Record<SessionIntent, SessionType[]> = {
  select_attributes: ['engage', 'gather'],
  converge_values: ['gather'],
  select_item_pool: ['gather', 'arrange'],
  rank_priorities: ['arrange'],
  calibration: ['gather', 'arrange'],
  review: ['arrange'],
};

/**
 * Canonical subject and decision-kind mappings derived from session intent.
 * These are the preferred runtime semantics; legacy sessionType remains for compatibility.
 */
export const SESSION_INTENT_TO_CANONICAL_SESSION_TYPE: Record<SessionIntent, CanonicalSessionType> = {
  select_attributes: 'criteria',
  converge_values: 'values',
  select_item_pool: 'item_pool',
  rank_priorities: 'items',
  calibration: 'criteria',
  review: 'items',
};

export const SESSION_INTENT_TO_DECISION_CLASS_KIND: Record<SessionIntent, CanonicalDecisionClassKind> = {
  select_attributes: 'selection',
  converge_values: 'convergence',
  select_item_pool: 'selection',
  rank_priorities: 'force_ranking',
  calibration: 'review',
  review: 'review',
};

export const LEGACY_SESSION_TYPE_BY_INTENT: Record<SessionIntent, SessionType> = {
  select_attributes: 'engage',
  converge_values: 'gather',
  select_item_pool: 'gather',
  rank_priorities: 'arrange',
  calibration: 'gather',
  review: 'arrange',
};

export function getCanonicalSessionTypeForIntent(intent: SessionIntent): CanonicalSessionType {
  return SESSION_INTENT_TO_CANONICAL_SESSION_TYPE[intent];
}

export function getDecisionClassKindForIntent(intent: SessionIntent): CanonicalDecisionClassKind {
  return SESSION_INTENT_TO_DECISION_CLASS_KIND[intent];
}

export function getLegacySessionTypeForIntent(intent: SessionIntent): SessionType {
  return LEGACY_SESSION_TYPE_BY_INTENT[intent];
}

// ============================================================================
// Session Mode Configuration
// ============================================================================

/**
 * Session timing mode
 *
 * @deprecated Use SessionRunMode instead (Canon 23, Deviation D02).
 * Retained for backwards compatibility with existing data and the
 * Workflow cascade chain (default_timing_mode). New code must read
 * runMode; use deriveRunMode() to convert legacy pairs.
 *
 * LIVE - Real-time collaborative session (everyone participates simultaneously)
 * ASYNC - Asynchronous session that's open for a period and then closes
 */
export type SessionTimingMode = 'live' | 'async';

/**
 * Session entry mode - who enters data into the system
 *
 * @deprecated Use SessionRunMode instead (Canon 23, Deviation D02).
 * Retained for backwards compatibility with existing data and the
 * Workflow cascade chain (default_entry_mode). New code must read
 * runMode; use deriveRunMode() to convert legacy pairs.
 *
 * FACILITATOR - Facilitator enters all responses on behalf of the group
 * PARTICIPANTS - Each participant enters their own responses
 */
export type SessionEntryMode = 'facilitator' | 'participants';

/**
 * Session run mode — the canonical, single-field replacement for the
 * timingMode × entryMode pair (Canon 23 §6.2, Deviation D02).
 *
 * FACILITATOR_LIVE - A facilitator drives the session in real time and
 *   enters all responses (optionally on behalf of participants who
 *   verbalize them). Facilitator controls pacing, reveal, finalization.
 *
 * PARTICIPANT_LIVE - All participants enter their own responses in real
 *   time. Aggregation is live. Facilitator controls the reveal gate
 *   (hidden → live → finalized).
 *
 * OPEN - The session exists between two time horizons (opensAt/closesAt).
 *   Participants respond asynchronously at their own pace. No facilitator
 *   is required during the session; the facilitator reviews and finalizes
 *   after close. Replaces the legacy 'async' timing mode.
 */
export type SessionRunMode = 'facilitator_live' | 'participant_live' | 'open';

/**
 * Derive the canonical run mode from a legacy (timingMode, entryMode) pair.
 *
 * The one rule (canon 21 §6.2; the same rule the run_mode column trigger,
 * migrations 526 and 1045, and the server's session defaults, migration 1039,
 * use — keep them equal):
 *   async                → open (whoever enters the data: canon has no async
 *                          run mode but `open`)
 *   otherwise participants → participant_live
 *   otherwise            → facilitator_live
 *
 * Until 1045 this returned facilitator_live for async + facilitator while the
 * column said open, so one session carried two run modes. A surface whose
 * people answer in the product's live room must say so with an explicit
 * runMode (see legacyModesForRunMode), not lean on the timing alone.
 */
export function deriveRunMode(
  timingMode: SessionTimingMode | string | null | undefined,
  entryMode: SessionEntryMode | string | null | undefined
): SessionRunMode {
  if (timingMode === 'async') return 'open';
  return entryMode === 'participants' ? 'participant_live' : 'facilitator_live';
}

const RUN_MODES: readonly SessionRunMode[] = ['facilitator_live', 'participant_live', 'open'];

/** True when `value` is one of the three canonical run modes. */
export function isSessionRunMode(value: unknown): value is SessionRunMode {
  return typeof value === 'string' && (RUN_MODES as readonly string[]).includes(value);
}

/**
 * The run mode a saved `mode_config` stands for, read the way the column
 * trigger reads it (migration 1045): its `runMode` when that is a run mode,
 * else the legacy snake_case `run_mode` key, else derived from the legacy pair.
 * The save path writes this to `prioritization_sessions.run_mode`, so the
 * column and `mode_config.runMode` are one value.
 */
export function runModeOfSavedConfig(modeConfig: Record<string, unknown>): SessionRunMode {
  if (isSessionRunMode(modeConfig['runMode'])) return modeConfig['runMode'];
  if (isSessionRunMode(modeConfig['run_mode'])) return modeConfig['run_mode'];
  return deriveRunMode(modeConfig['timingMode'] as string | undefined, modeConfig['entryMode'] as string | undefined);
}

/**
 * Project a run mode back onto the legacy pair, for persistence paths and
 * cascade layers that still carry timingMode/entryMode.
 */
export function legacyModesForRunMode(runMode: SessionRunMode): {
  timingMode: SessionTimingMode;
  entryMode: SessionEntryMode;
} {
  switch (runMode) {
    case 'facilitator_live':
      return { timingMode: 'live', entryMode: 'facilitator' };
    case 'participant_live':
      return { timingMode: 'live', entryMode: 'participants' };
    case 'open':
      return { timingMode: 'async', entryMode: 'participants' };
  }
}

/**
 * Resolve the effective run mode for a SessionModeConfig.
 * Prefers the explicit runMode field; falls back to deriving from the
 * legacy pair so pre-migration configs keep working.
 */
export function resolveRunMode(config: SessionModeConfig): SessionRunMode {
  return runModeOfSavedConfig(config as unknown as Record<string, unknown>);
}

/**
 * Session mode configuration
 */
export interface SessionModeConfig {
  /**
   * Canonical run mode (Canon 23 §6.2). When present, this wins over the
   * legacy timingMode/entryMode pair. Optional only for backwards
   * compatibility with pre-migration data — all new sessions must set it.
   * Read via resolveRunMode(), never directly with a fallback of your own.
   */
  runMode?: SessionRunMode;

  /** @deprecated Derived from runMode for new sessions; see SessionTimingMode. */
  timingMode: SessionTimingMode;

  /** @deprecated Derived from runMode for new sessions; see SessionEntryMode. */
  entryMode: SessionEntryMode;

  // For open (async) mode: when the session opens for participation.
  // Required when runMode === 'open'.
  opensAt?: Date;

  // For open (async) mode: when the session closes for participation.
  // Required when runMode === 'open'.
  closesAt?: Date;

  // For live modes: expected duration in minutes
  expectedDuration?: number;

  // Whether participants can see each other's responses before submission
  allowViewOtherResponses?: boolean;

  // Whether participants can change their responses after initial submission
  allowResponseChanges?: boolean;

  // For participant entry: require all participants to respond before proceeding
  requireAllResponses?: boolean;

  /**
   * When set, this session is a re-run of the given session ID.
   * The session starts in DRAFT and stays in RUN_COMPLETE (not auto-published)
   * until the facilitator decides what to do with the two sets of results.
   */
  reRunOf?: string;

  /**
   * An ask (migration 1054): one person asked for an estimate. The person asked
   * finishes it (complete_ask_answer); the person who asked sees it waiting.
   */
  ask?: boolean;
}

/**
 * Session state invariants
 */
export const SESSION_STATE_INVARIANTS: Record<SessionState, string[]> = {
  DRAFT: [
    'Session has facilitator',
    'Can modify criteria, participants, candidates',
  ],
  CRITERIA_FINALIZED: [
    'Criteria set is locked',
    'Weights are defined',
    'Can add/remove candidates',
    'Cannot run method yet (candidates must be eligible)',
  ],
  RUN_COMPLETE: [
    'Tool has been executed',
    'Ranking output exists',
    'Can apply overrides',
    'Can still modify output before publish',
  ],
  PUBLISHED: [
    'Results are final and shared',
    'Output artifact generated',
    'Cannot modify without creating new session',
  ],
};

// ============================================================================
// Session Criteria Configuration
// ============================================================================

/**
 * Session-scoped criteria configuration
 * Criteria are selected from attributes but configured per-session
 */
export interface SessionCriteriaConfig {
  // Which attributes are being used as criteria for this session
  criteriaIds: string[];
  
  // Weights for each criterion (must sum to 1.0 or 100)
  weights: Record<string, number>;
  
  // Which attributes MUST have accepted values for an item to be eligible
  reconciliationRequiredSet: string[];
  
  // Optional framing statement for the session
  framingStatement?: string;
}

/**
 * Default criteria configuration
 */
export const DEFAULT_CRITERIA_CONFIG: SessionCriteriaConfig = {
  criteriaIds: [],
  weights: {},
  reconciliationRequiredSet: [],
};

// ============================================================================
// Item Eligibility (session-scoped)
// ============================================================================

/**
 * Item eligibility status for a session
 * 
 * INELIGIBLE_STRUCTURAL - Missing required attributes entirely
 * ELIGIBLE_WITH_GAPS - Has attributes but missing accepted values
 * ELIGIBLE - Ready to be included in prioritization run
 */
export type ItemEligibilityStatus = 
  | 'INELIGIBLE_STRUCTURAL' 
  | 'ELIGIBLE_WITH_GAPS' 
  | 'ELIGIBLE';

/** @deprecated Use ItemEligibilityStatus */
export type CandidateEligibilityStatus = ItemEligibilityStatus;

/**
 * Detailed eligibility information for an item in a session
 */
export interface ItemEligibility {
  status: ItemEligibilityStatus;
  
  // Attributes required by session that item doesn't have
  missingAttributes: string[];
  
  // Attributes that exist but don't have accepted values
  missingAcceptedValues: string[];
  
  // Whether exceptions have been accepted for missing values
  hasExceptions: boolean;
  
  // Specific exceptions that have been accepted
  acceptedExceptions: string[];
  
  // Last computed timestamp
  computedAt: Date;
}

/** @deprecated Use ItemEligibility */
export type CandidateEligibility = ItemEligibility;

/**
 * Compute eligibility status from details
 */
export function computeEligibilityStatus(
  missingAttributes: string[],
  missingAcceptedValues: string[],
  hasExceptions: boolean
): ItemEligibilityStatus {
  if (missingAttributes.length > 0) {
    return 'INELIGIBLE_STRUCTURAL';
  }
  if (missingAcceptedValues.length > 0 && !hasExceptions) {
    return 'ELIGIBLE_WITH_GAPS';
  }
  return 'ELIGIBLE';
}

// ============================================================================
// Session Output Types
// ============================================================================

/**
 * Rank override — the canon 01 Override stage: a decision-authority holder
 * moves one item, with a required rationale. Written only by the governed
 * override_session_rank RPC (migration 768), which appends it to
 * ranking_output.overrides; read back from JSON, so overriddenAt arrives as an
 * ISO string.
 */
export interface RankOverride {
  id: string;
  itemId: string;
  originalRank: number;
  newRank: number;
  rationale: string;
  overriddenBy: string;
  /** Display name snapshotted at write time (canon 27 §7 invariant 3). */
  overriddenByName?: string | null;
  overriddenAt: Date | string;
  /** 'tie_break' when the facilitator broke a tie (S-5, migration 973). */
  kind?: 'move' | 'tie_break';
}

/**
 * Session ranking output - results from running a prioritization tool
 */
export interface SessionRankingOutput {
  // Ordered list of item IDs by rank
  rankedItemIds: string[];
  
  // Scores for each item (tool-dependent)
  scores: Record<string, Record<string, number>>;

  // Classification detail for each item (tool-dependent, category-oriented tools)
  classifications?: Record<string, Record<string, string>>;
  
  // Normalized scores (0-100 scale)
  normalizedScores: Record<string, number>;
  
  // Tier assignments (if applicable)
  tiers: Record<string, string>;
  
  // Manual overrides applied after tool run
  overrides: RankOverride[];
  
  // Tool that generated this output
  toolId: string;
  toolName: string;
  
  // Configuration used
  toolConfig: Record<string, unknown>;
  
  // Timestamps
  generatedAt: Date;
  lastModifiedAt: Date;

  /** Outcome artifact metadata (e.g. INCLUSION vs FORCE_RANKED; tie-break rule for force-ranked). Stored in outcome only. */
  outcomeMetadata?: OutcomeArtifactMetadata;
}

/** True when the session has at least one ranked prioritization result (in-memory / ranking_output). */
export function sessionHasPrioritizationResults(session: {
  rankingOutput?: SessionRankingOutput | null;
}): boolean {
  const ro = session.rankingOutput;
  if (!ro) return false;
  if (Array.isArray(ro.rankedItemIds) && ro.rankedItemIds.length > 0) return true;
  const ordered = (ro as { orderedItems?: Array<{ id?: string }> }).orderedItems;
  return Array.isArray(ordered) && ordered.some((o) => Boolean(o?.id));
}

// ============================================================================
// Session Decision Types
// ============================================================================

/**
 * Types of decisions that can be made in a session
 */
export type SessionDecisionType = 
  | 'criteria_selection'   // Choosing which criteria to use
  | 'criteria_weighting'   // Setting weights for criteria
  | 'value_acceptance'     // Accepting a proposed value
  | 'exception_accepted'   // Accepting an unknown value exception
  | 'rank_override'        // Manually overriding a rank
  | 'cut_line_set'         // Setting the cut line position
  | 'candidate_inclusion'  // Adding an item to candidates
  | 'candidate_exclusion'  // Removing an item from candidates
  | 'publish';             // Publishing the results

/**
 * Record of a decision made during a session
 */
export interface SessionDecision {
  id: string;
  type: SessionDecisionType;
  description: string;
  
  // Who made this decision
  madeBy: string[];
  madeAt: Date;
  
  // Supporting information
  rationale?: string;
  
  // Related entities
  relatedItemId?: string;
  relatedAttributeKey?: string;
  
  // For value acceptance decisions
  previousValue?: unknown;
  newValue?: unknown;
  
  // For rank override decisions
  previousRank?: number;
  newRank?: number;
}

// ============================================================================
// Main Session Entity
// ============================================================================

/**
 * Session context mode (Stage 2).
 * - cycle_linked: Session is tied to a track; track_id required; full lock snapshots.
 * - app_linked: Session may exist without track; session-local lock only.
 * - isolated: Session is standalone; no track; session-local lock only.
 * When absent (e.g. from DB before migration), infer: trackId present => cycle_linked; else app_linked.
 */
export type SessionContextMode = 'cycle_linked' | 'app_linked' | 'isolated';

/**
 * Prioritization Session
 * 
 * A collaborative prioritization event with:
 * - Facilitator and participants
 * - Session-scoped criteria selection
 * - Candidate items to prioritize
 * - Tool configuration and output
 * - Decision record
 */
export interface PrioritizationSession {
  id: string;
  name: string;
  description?: string;

  /**
   * Canonical in-memory workspace scoping field (DB column: workspace_id).
   * sessionStore sets/reads this pervasively; kept optional since
   * standalone/legacy sessions may not carry it.
   */
  workspaceId?: string;

  // ================================
  // Legacy Session Type Compatibility
  // ================================
  
  /**
   * Legacy sessionType retained for backward compatibility.
   * Canonical runtime semantics should be derived from `intent`:
   * - Canonical Session Type = what is being prioritized
   * - Canonical Decision Class = what kind of judgment is being made
   */
  sessionType: SessionType;
  
  // ================================
  // Association
  // ================================
  
  // Optional parent DEGAP cycle (null = standalone session)
  cycleId?: string;
  
  /**
   * Parent EGAp iteration. Sessions should belong to iterations, not directly to cycles.
   * Per the parent-child model: Catchball Loops → EGAp Iterations → Sessions
   * 
   * When set, this session is part of a structured EGAp workflow.
   * When null, this is a standalone/ad-hoc session.
   */
  egapIterationId?: string;
  
  /**
   * Session context mode. When absent, inferred: trackId present => cycle_linked; else app_linked.
   */
  sessionContextMode?: SessionContextMode;

  /**
   * The session's Deliberation Record (Canon 23 §6.4) — first-class signal
   * capture: chat log, recordings, transcript enrichment, item rationale,
   * override justifications. One per session; created with the session.
   * See src/types/prioritization/deliberation.ts.
   */
  deliberationRecordId?: string;

  /**
   * Track this session belongs to (required when sessionContextMode is cycle_linked).
   * Optional for app_linked/isolated. Stage 2B will make DB column nullable.
   */
  trackId?: string | null;

  /**
   * The Catchball Ball this session was run in response to.
   * Null for sessions outside a Catchball loop.
   * Closes the traceability chain: Cycle → Loop → Ball → Session → Proposal.
   */
  catchballBallId?: string | null;
  
  /**
   * Session intent - declares the purpose of this session for gate validation.
   * Required for sessions to count towards cycle progression gates.
   * 
   * - select_attributes: Prioritize attributes to decide which criteria matter
   * - converge_values: Produce consensus values for attributes on items
   * - select_item_pool: Decide which items make the cut for ranking
   * - rank_priorities: Force-rank the selected candidate pool
   * - calibration: Calibration/review session
   * - review: Structured comparative review without ranking
   */
  intent: SessionIntent;
  
  /**
   * ID of the cycle configuration session that this session inherits from.
   * When set, criteria, weights, tools, and value providers are inherited.
   */
  configSessionId?: string;
  
  // Resulting item pool (created after publish)
  resultingItemPoolId?: string;

  /**
   * The prioritization context this session belongs to.
   * Set when a session is created from a configuration-aware flow
   * (e.g. from ConfigurationSelector → the Event Designer).
   * Links the session into the condition resolver, stage engine, and decision unit.
   */
  contextId?: string;

  /** Product module that created this session (migration 490). Default: prioritization. */
  originModule?: import('./originModule').OriginModule;

  /** Collaboration space this session belongs to (migration 492). NULL = workspace-wide / org-level. */
  spaceId?: string | null;

  /**
   * Workspace this session belongs to. Set at creation from CreateSessionParams
   * (sessionStore.createSession) and persisted to the `workspace_id` column;
   * many call sites read it with a `?? getCurrentWorkspaceId()` fallback for
   * sessions created before this field existed. Was previously read/written
   * throughout sessionStore.ts and SessionDetail.tsx without being declared
   * here — declaring it so those call sites type-check against the real shape.
   */
  workspaceId?: string;

  // ================================
  // Inherited Configuration (from Cycle Config Session)
  // ================================
  
  /**
   * Whether configuration was inherited from a locked cycle config session.
   * When true, certain settings cannot be changed at the session level.
   */
  inheritedFromCycleConfig?: boolean;
  
  /**
   * Value provider configurations inherited from cycle config.
   * Controls who can propose/accept values for which attributes.
   */
  valueProviderConfigs?: ValueProviderConfig[];
  
  // ================================
  // Catchball Integration
  // ================================
  
  /**
   * ID of the Catchball output that seeded this session.
   * When set, the session was created from Catchball-aligned priorities.
   */
  catchBallOutputId?: string;
  
  /**
   * Whether this session was seeded from Catchball output.
   * When true, initial priorities came from the Catchball process.
   */
  seededFromCatchball?: boolean;
  
  /**
   * Initial ranking inherited from Catchball output.
   * Maps itemId to the rank assigned during Catchball.
   */
  catchBallInitialRanking?: Record<string, number>;
  
  // ================================
  // Roles
  // ================================
  
  facilitatorId: string;
  participantIds: string[];
  
  // ================================
  // Criteria Configuration
  // ================================
  
  criteriaConfig: SessionCriteriaConfig;
  
  // ================================
  // Items (session item set)
  // ================================
  
  // IDs of items in this session's set (used for cycle_linked and app_linked modes)
  itemIds: string[];

  /**
   * Free-form items for isolated sessions. Each entry is a user-typed label
   * that exists only within this session — no link to the system item registry.
   */
  isolatedItems?: string[];

  /**
   * For session-first creation (Path C): the meta-category the user chose
   * when asked "What do you want to prioritize?"
   * Drives item filtering and surfaces the intent of the session.
   * One of: people | places | things | activities | information | time
   */
  selectedMetaCategory?: import('../prioritization/lifecycle').ItemMetaCategory;
  
  // Computed eligibility for each item
  itemEligibility: Record<string, ItemEligibility>;
  
  // ================================
  // Comparison Cell Filtering (Altitude x Candidate Type)
  // ================================
  
  /**
   * Selected cells from the 5x6 altitude/type grid.
   * When set, only candidates matching these cells are eligible.
   * Single cell = default mode. Multiple cells = override mode.
   */
  selectedGridCells?: GridCellFilter[];
  
  // ================================
  // Tool Configuration
  // ================================
  
  selectedToolId?: string;
  toolConfig?: Record<string, unknown>;

  /**
   * Declared description of the room (DB: situation_vector, migration 715).
   * Facilitator-supplied, visible to every participant, never inferred.
   * Absent = not declared; the method recommendation then depends on the
   * decision's shape alone. See src/types/prioritization/situationVector.ts.
   */
  situationVector?: SituationVector | null;
  /** SITUATION_VECTOR_VERSION the stored vector was written against (DB: situation_vector_version). */
  situationVectorVersion?: number | null;

  // ================================
  // Output
  // ================================
  
  // Reference to the tool run record
  toolRunId?: string;

  /**
   * Pre-S6 Results: the system recommendation produced by the S1-S5 model.
   * Captured as a snapshot when S6 opens; never overwritten.
   * Corresponds to `pre_s6_ranking_snapshot` on the DB row.
   */
  preS6RankingSnapshot?: SessionRankingOutput;

  // Ranking output from the tool
  rankingOutput?: SessionRankingOutput;

  /**
   * Post-S6 Results: the group recommendation produced after S6 deliberation.
   * Stored once the S6 session reaches DECIDED; never overwritten by the
   * subsequent Final Ranking (Official Decision).
   * Corresponds to `post_s6_ranking_snapshot` on the DB row.
   */
  postS6GroupRecommendation?: SessionRankingOutput;

  // Cut line position (items above are "in", items below are "out")
  cutLinePosition?: number;

  /**
   * Nested prioritization event embedded inside this S1-S5 session.
   *
   * Any S1-S5 session may carry exactly one nested prioritization event that
   * records a lightweight selection decision (cut-line or Q&A) produced during
   * the session's work — without requiring a full S6 force-rank.
   *
   * For S6 sessions, this field is unused; the session's `rankingOutput` and
   * `cutLinePosition` are the authoritative ranking artifacts.
   */
  nestedPrioritizationEvent?: NestedPrioritizationEvent;
  
  // ================================
  // State
  // ================================
  
  state: SessionState;
  
  // ================================
  // Lock Semantics (v2.2 Canon)
  // ================================
  
  /**
   * When session configuration was locked.
   * Per Canon: "Configuration locks at session start. Post-lock changes require
   * a new session instance."
   * 
   * Once set, the following are immutable:
   * - criteriaConfig
   * - selectedToolId
   * - toolConfig
   * - modeConfig
   * - itemIds (pool membership)
   */
  configurationLockedAt?: Date;
  
  /**
   * User who locked the configuration (typically facilitator starting the session)
   */
  configurationLockedBy?: string;
  
  /**
   * Snapshot of the item pool at the time of lock.
   * Per Canon: "A Run operates over a locked Item Pool"
   */
  poolSnapshotId?: string;
  
  /**
   * Snapshot of the criteria set at the time of lock.
   * Per Canon: "A Run operates over a locked Criteria Set"
   */
  criteriaSnapshotId?: string;
  
  /**
   * Snapshot of participation configuration at time of lock.
   */
  participationSnapshotId?: string;
  
  // ================================
  // Timestamps
  // ================================
  
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  
  criteriaFinalizedAt?: Date;
  runCompletedAt?: Date;
  publishedAt?: Date;
  publishedBy?: string;

  /**
   * Retired (mig 758 retire_session) — the user-facing "delete". The row and
   * everything under it stay; lists hide it; restore_retired_session takes it
   * back. Written only by those RPCs (DB gate), never by a client update.
   */
  retiredAt?: Date | string | null;
  retiredBy?: string | null;
  retiredReason?: string | null;
  
  // Scheduled time (optional)
  scheduledFor?: Date;
  scheduledDuration?: number; // minutes

  // Video meeting link (optional - generated via Zoom or Google Meet connector)
  meetingLink?: string;
  meetingProvider?: 'zoom' | 'google-meet';
  
  // ================================
  // Session Mode Configuration
  // ================================
  
  // How the session operates (live vs async, facilitator vs participant entry)
  modeConfig: SessionModeConfig;
  
  // ================================
  // Notes and Decisions
  // ================================
  
  sessionNotes?: string;
  decisions: SessionDecision[];

  /** AI-generated strategic narrative persisted to DB. Null until generated. */
  strategicNarrative?: string | null;

  // ================================
  // Metadata
  // ================================

  tags?: string[];
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Session Participant Types
// ============================================================================

/**
 * Role a participant plays in a session
 */
export type SessionParticipantRole = 
  | 'facilitator'     // Runs the session
  | 'prioritizer'     // Participates in ranking decisions
  | 'observer'        // Can view but not participate
  | 'attribute_owner'; // Provides values for specific attributes

/**
 * Participant in a session
 */
export interface SessionParticipant {
  userId: string;
  name: string;
  email?: string;
  role: SessionParticipantRole;
  
  // Which attributes this participant owns (for attribute_owner role)
  ownedAttributeKeys?: string[];
  
  // Status
  joinedAt: Date;
  status: 'invited' | 'active' | 'inactive';
  
  // Presence tracking
  lastActiveAt?: Date;
}

// ============================================================================
// Gather Session (GATHER Phase - Shaping Session)
// ============================================================================

/**
 * Gather Session State Machine
 * 
 * - draft: Session created, not started
 * - collecting: Actively collecting items
 * - shaping: Items being shaped and structured
 * - review: Items ready for review
 * - complete: All items shaped and ready for ARRANGE
 */
export type GatherSessionState = 
  | 'draft'
  | 'collecting'
  | 'shaping'
  | 'review'
  | 'complete';

/**
 * Item readiness tracking within a gather session
 */
export interface GatherSessionItemStatus {
  itemId: string;
  readinessState: 'draft' | 'needs_info' | 'review_ready' | 'shaped';
  missingAttributes?: string[];
  lastUpdatedAt: Date;
  lastUpdatedBy: string;
}

/**
 * GatherSession - Shaping Session for GATHER phase
 * 
 * A collaborative session focused on collecting, shaping, and structuring
 * candidate work items before they can be prioritized.
 * 
 * This session type handles:
 * - Item intake and collection
 * - Attribute completion
 * - Readiness assessment
 * - Shaping for prioritization
 */
export interface GatherSession {
  id: string;
  name: string;
  description?: string;
  
  // ================================
  // Session Type (DEGAP Alignment)
  // ================================
  
  /**
   * Session type - always 'gather' for GatherSession
   */
  sessionType: 'gather';
  
  // ================================
  // Association
  // ================================
  
  /** Parent DEGAP cycle */
  cycleId: string;
  
  /** Workspace this session belongs to */
  workspaceId?: string;
  
  // ================================
  // Configuration
  // ================================
  
  /**
   * Target number of items to gather (optional goal)
   */
  targetItemCount?: number;
  
  /**
   * Required attributes that must be filled for an item to be considered "shaped"
   */
  requiredAttributes: string[];
  
  /**
   * Minimum readiness percentage to complete the session
   */
  minReadinessPercent: number;
  
  // ================================
  // Items
  // ================================
  
  /**
   * Items being gathered/shaped in this session
   */
  itemIds: string[];
  
  /**
   * Status of each item in the session
   */
  itemStatuses: GatherSessionItemStatus[];
  
  // ================================
  // Participants
  // ================================
  
  participants: SessionParticipant[];
  
  // ================================
  // State
  // ================================
  
  state: GatherSessionState;
  
  // ================================
  // Progress Metrics
  // ================================
  
  /**
   * Number of items in each readiness state
   */
  readinessBreakdown: {
    draft: number;
    needs_info: number;
    review_ready: number;
    shaped: number;
  };
  
  /**
   * Overall readiness percentage (shaped / total * 100)
   */
  readinessPercent: number;
  
  // ================================
  // Timestamps
  // ================================
  
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  startedAt?: Date;
  completedAt?: Date;
  
  // ================================
  // Mode Configuration
  // ================================
  
  modeConfig: SessionModeConfig;
  
  // ================================
  // Notes
  // ================================
  
  sessionNotes?: string;
  
  // ================================
  // Metadata
  // ================================
  
  tags?: string[];
  metadata?: Record<string, unknown>;
}

/**
 * Create a default GatherSession
 */
export function createDefaultGatherSession(
  cycleId: string,
  createdBy: string,
  name: string = 'Item Work Session'
): GatherSession {
  const now = new Date();
  return {
    id: crypto.randomUUID(),
    name,
    sessionType: 'gather',
    cycleId,
    requiredAttributes: [],
    minReadinessPercent: 80,
    itemIds: [],
    itemStatuses: [],
    participants: [],
    state: 'draft',
    readinessBreakdown: {
      draft: 0,
      needs_info: 0,
      review_ready: 0,
      shaped: 0,
    },
    readinessPercent: 0,
    createdAt: now,
    createdBy,
    updatedAt: now,
    modeConfig: {
      runMode: 'open',
      timingMode: 'async',
      entryMode: 'participants',
    },
  };
}

// ============================================================================
// Session Transition Types
// ============================================================================

/**
 * Valid session state transitions
 */
export interface SessionTransition {
  from: SessionState;
  to: SessionState;
  action: string;
  allowedRoles: SessionParticipantRole[];
  guards: string[];
  effects: string[];
}

/**
 * All valid session transitions
 */
export const SESSION_TRANSITIONS: SessionTransition[] = [
  {
    from: 'DRAFT',
    to: 'CRITERIA_FINALIZED',
    action: 'finalizeCriteria',
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    guards: [
      'criteria defined',
      'weights set (sum to 100 or 1.0)',
      'reconciliation required set defined',
    ],
    effects: [
      'Lock criteria configuration',
      'Compute candidate eligibility',
    ],
  },
  {
    from: 'CRITERIA_FINALIZED',
    to: 'RUN_COMPLETE',
    action: 'runMethod',
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    guards: [
      'Tool selected and configured',
      'At least one ELIGIBLE candidate',
      'All ELIGIBLE_WITH_GAPS have exceptions OR are excluded',
    ],
    effects: [
      'Execute tool',
      'Store ranking output',
      'Update item states to PRIORITIZED',
    ],
  },
  {
    from: 'RUN_COMPLETE',
    to: 'PUBLISHED',
    action: 'publish',
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    guards: [
      'Ranking output exists',
      'Cut line set (optional but recommended)',
    ],
    effects: [
      'Generate published artifact',
      'Create/update Item Pool if configured',
      'Notify participants',
    ],
  },
  // Backward transitions
  {
    from: 'CRITERIA_FINALIZED',
    to: 'DRAFT',
    action: 'reopenCriteria',
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    guards: [],
    effects: [
      'Unlock criteria configuration',
      'Clear candidate eligibility cache',
    ],
  },
  {
    from: 'RUN_COMPLETE',
    to: 'CRITERIA_FINALIZED',
    action: 'rerunMethod',
    allowedRoles: ['workspace-admin', 'tenant-admin', 'platform-admin'],
    guards: [],
    effects: [
      'Clear ranking output',
      'Reset item states',
    ],
  },
];

/**
 * Check if a session transition is valid
 */
export function canSessionTransition(
  currentState: SessionState,
  targetState: SessionState
): { allowed: boolean; transition?: SessionTransition; reason?: string } {
  const transition = SESSION_TRANSITIONS.find(
    t => t.from === currentState && t.to === targetState
  );
  
  if (!transition) {
    return {
      allowed: false,
      reason: `No valid transition from ${currentState} to ${targetState}`,
    };
  }
  
  return {
    allowed: true,
    transition,
  };
}

// ============================================================================
// Session Helpers
// ============================================================================

/**
 * Create a new empty session
 */
export function createEmptySession(
  name: string,
  facilitatorId: string,
  createdBy: string,
  cycleId?: string,
  intent: SessionIntent = 'rank_priorities'
): Omit<PrioritizationSession, 'id'> {
  const now = new Date();
  
  return {
    name,
    facilitatorId,
    cycleId,
    intent,
    sessionType: getLegacySessionTypeForIntent(intent),
    participantIds: [facilitatorId],
    criteriaConfig: { ...DEFAULT_CRITERIA_CONFIG },
    itemIds: [],
    itemEligibility: {},
    state: 'DRAFT',
    createdAt: now,
    createdBy,
    updatedAt: now,
    decisions: [],
    modeConfig: {
      // Legacy degenerate pair (async + facilitator); canonically a
      // facilitator-driven session per deriveRunMode().
      runMode: 'facilitator_live',
      timingMode: 'async',
      entryMode: 'facilitator',
    },
  };
}

/**
 * Get eligible items from a session
 */
export function getEligibleItems(
  session: PrioritizationSession
): string[] {
  return session.itemIds.filter(id => {
    const eligibility = session.itemEligibility[id];
    return eligibility?.status === 'ELIGIBLE';
  });
}

/**
 * Get items that need attention (missing values)
 */
export function getItemsWithGaps(
  session: PrioritizationSession
): string[] {
  return session.itemIds.filter(id => {
    const eligibility = session.itemEligibility[id];
    return eligibility?.status === 'ELIGIBLE_WITH_GAPS';
  });
}

/**
 * Get items that are not eligible for ranking
 */
export function getIneligibleItems(
  session: PrioritizationSession
): string[] {
  return session.itemIds.filter(id => {
    const eligibility = session.itemEligibility[id];
    return eligibility?.status === 'INELIGIBLE_STRUCTURAL';
  });
}

// ============================================================================
// Session Audit Types
// ============================================================================

/**
 * Session audit event
 */
export interface SessionAuditEvent {
  id: string;
  sessionId: string;
  eventType: 
    | 'created' 
    | 'state_changed' 
    | 'criteria_updated'
    | 'participant_added'
    | 'participant_removed'
    | 'candidate_added'
    | 'candidate_removed'
    | 'tool_run'
    | 'override_applied'
    | 'published';
  actorId: string;
  timestamp: Date;
  details: Record<string, unknown>;
}

// ============================================================================
// Session Creation from Cycle Config
// ============================================================================

import type {
  CycleConfigurationSession,
  CriteriaDecisionValue,
  WeightsDecisionValue,
  ToolsDecisionValue,
  ValueProvidersDecisionValue,
  ParticipantsDecisionValue,
} from './cycleConfigSession';

/**
 * Options for creating a session from cycle configuration
 */
export interface CreateSessionFromConfigOptions {
  /** Name for the new session */
  name: string;
  
  /** Description for the new session */
  description?: string;
  
  /** User creating the session */
  createdBy: string;
  
  /** Cycle ID */
  cycleId: string;
  
  /** The locked configuration session to inherit from */
  configSession: CycleConfigurationSession;
  
  /** Candidate IDs to include */
  itemIds?: string[];
  
  /** Additional session metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Create a prioritization session that inherits from a locked cycle config session
 * 
 * This ensures that:
 * - Criteria are inherited and locked from cycle config
 * - Weights are inherited and locked from cycle config
 * - Tool selection is inherited and locked from cycle config
 * - Value provider configs are inherited
 * - Participants are pre-populated from cycle config
 */
export function createSessionFromCycleConfig(
  options: CreateSessionFromConfigOptions
): PrioritizationSession | { error: string } {
  const { name, description, createdBy, cycleId, configSession, itemIds, metadata } = options;
  
  // Ensure config session is locked
  if (configSession.state !== 'locked') {
    return { 
      error: 'Configuration session must be locked before creating prioritization sessions' 
    };
  }
  
  // Extract locked configuration
  const lockedConfig = extractLockedConfiguration(configSession);
  
  if (!lockedConfig) {
    return { 
      error: 'Failed to extract locked configuration from config session' 
    };
  }
  
  const now = new Date();
  const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Build criteria config from locked decisions
  const criteriaConfig: SessionCriteriaConfig = {
    selectedCriteriaIds: lockedConfig.criteria?.selectedCriteriaIds || [],
    criteriaWeights: lockedConfig.weights?.weights || {},
    minimumEligibleCriteria: lockedConfig.criteria?.selectedCriteriaIds.length || 0,
    reconciliationRequired: new Set(lockedConfig.criteria?.selectedCriteriaIds || []),
  };
  
  // Build participant list from locked decisions
  const participantIds: string[] = lockedConfig.participants?.sessionParticipants
    .map(p => p.userId) || [];
  
  // Find facilitator from participants
  const facilitator = lockedConfig.participants?.sessionParticipants
    .find(p => p.sessionRole === 'facilitator');
  const facilitatorId = facilitator?.userId || createdBy;
  
  // Build mode config from config session, resolving the canonical run mode
  // from the legacy pair the config session carries.
  const modeConfig: SessionModeConfig = {
    runMode: deriveRunMode(configSession.timingMode, configSession.entryMode),
    timingMode: configSession.timingMode,
    entryMode: configSession.entryMode,
  };
  
  // Create the session with inherited config
  const session: PrioritizationSession = {
    id: sessionId,
    name,
    description,
    
    // Association
    cycleId,
    configSessionId: configSession.id,
    inheritedFromCycleConfig: true,
    
    // Inherited value provider configs
    valueProviderConfigs: lockedConfig.valueProviders?.attributeConfigs,
    
    // Roles
    facilitatorId,
    participantIds,
    
    // Criteria (inherited and locked)
    criteriaConfig,
    
    // Candidates
    itemIds: itemIds || [],
    itemEligibility: {},
    
    // Tool (inherited and locked)
    selectedToolId: lockedConfig.tools?.primaryToolId || lockedConfig.tools?.selectedToolIds[0],
    toolConfig: lockedConfig.tools?.toolConfigs[lockedConfig.tools?.primaryToolId || ''],
    
    // State
    state: 'DRAFT',
    
    // Mode config
    modeConfig,
    
    // Decisions and notes
    decisions: [],
    
    // Timestamps
    createdAt: now,
    createdBy,
    updatedAt: now,
    
    // Metadata
    metadata,
  };
  
  return session;
}

/**
 * Extract locked configuration from a config session
 * (Helper function since we can't import getLockedConfiguration due to circular deps)
 */
function extractLockedConfiguration(
  session: CycleConfigurationSession
): {
  criteria?: CriteriaDecisionValue;
  weights?: WeightsDecisionValue;
  tools?: ToolsDecisionValue;
  valueProviders?: ValueProvidersDecisionValue;
  participants?: ParticipantsDecisionValue;
} | null {
  if (session.state !== 'locked') {
    return null;
  }
  
  const result: ReturnType<typeof extractLockedConfiguration> = {};
  
  for (const decision of session.decisions) {
    if (decision.status === 'locked') {
      switch (decision.type) {
        case 'criteria':
          result.criteria = decision.value as CriteriaDecisionValue;
          break;
        case 'weights':
          result.weights = decision.value as WeightsDecisionValue;
          break;
        case 'tools':
          result.tools = decision.value as ToolsDecisionValue;
          break;
        case 'value_providers':
          result.valueProviders = decision.value as ValueProvidersDecisionValue;
          break;
        case 'participants':
          result.participants = decision.value as ParticipantsDecisionValue;
          break;
      }
    }
  }
  
  return result;
}

// ============================================================================
// Session Creation from Catchball Output
// ============================================================================

import type { CatchBallOutput } from './catchball';

/**
 * Options for creating a session from Catchball output
 */
export interface CreateSessionFromCatchballOptions {
  /** Name for the new session */
  name: string;
  
  /** Description for the new session */
  description?: string;
  
  /** User creating the session */
  createdBy: string;
  
  /** Cycle ID */
  cycleId: string;
  
  /** The Catchball output to use */
  catchBallOutput: CatchBallOutput;
  
  /** Facilitator ID (defaults to createdBy) */
  facilitatorId?: string;
  
  /** Filter priorities by time horizon */
  timeHorizonFilter?: 'get_house_in_order' | 'reinforce_foundation' | 'create_the_future';
  
  /** Additional session metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Create a prioritization session seeded from Catchball output
 * 
 * This creates a session pre-populated with:
 * - Candidate IDs from the aligned priorities
 * - Initial ranking based on Catchball final ranks
 * - Reference to the source Catchball output
 */
export function createSessionFromCatchball(
  options: CreateSessionFromCatchballOptions
): PrioritizationSession {
  const {
    name,
    description,
    createdBy,
    cycleId,
    catchBallOutput,
    facilitatorId,
    timeHorizonFilter,
    metadata,
  } = options;
  
  const now = new Date();
  const sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  // Filter priorities by time horizon if specified
  let priorities = catchBallOutput.alignedPriorities;
  if (timeHorizonFilter) {
    priorities = priorities.filter(p => p.timeHorizon === timeHorizonFilter);
  }
  
  // Build candidate IDs and initial ranking
  const itemIds = priorities.map(p => p.itemId);
  const catchBallInitialRanking: Record<string, number> = {};
  priorities.forEach(p => {
    catchBallInitialRanking[p.itemId] = p.finalRank;
  });
  
  // Default mode config
  const modeConfig: SessionModeConfig = {
    runMode: 'facilitator_live',
    timingMode: 'live',
    entryMode: 'facilitator',
  };
  
  // Default criteria config (can be overridden)
  const criteriaConfig: SessionCriteriaConfig = {
    selectedCriteriaIds: [],
    criteriaWeights: {},
    minimumEligibleCriteria: 0,
    reconciliationRequired: new Set(),
  };
  
  const session: PrioritizationSession = {
    id: sessionId,
    name,
    description,

    // Association
    cycleId,

    // Session type (catchball feeds into arrange/ranking sessions)
    sessionType: 'arrange',
    intent: 'rank_priorities',

    // Catchball integration
    catchBallOutputId: catchBallOutput.id,
    seededFromCatchball: true,
    catchBallInitialRanking,

    // Roles
    facilitatorId: facilitatorId || createdBy,
    participantIds: [],
    
    // Criteria (to be configured)
    criteriaConfig,
    
    // Candidates from Catchball
    itemIds,
    itemEligibility: {},
    
    // State
    state: 'DRAFT',
    
    // Mode config
    modeConfig,
    
    // Decisions and notes
    decisions: [],
    
    // Timestamps
    createdAt: now,
    createdBy,
    updatedAt: now,
    
    // Metadata
    metadata: {
      ...metadata,
      sourceType: 'catchball',
      catchBallGameId: catchBallOutput.gameId,
      alignmentScore: catchBallOutput.alignmentScore,
      timeHorizonFilter,
    },
  };
  
  return session;
}

/**
 * Get the initial ranking for candidates based on Catchball output
 */
export function getCatchballInitialRanking(
  session: PrioritizationSession
): { itemId: string; rank: number }[] {
  if (!session.seededFromCatchball || !session.catchBallInitialRanking) {
    return [];
  }
  
  return Object.entries(session.catchBallInitialRanking)
    .map(([itemId, rank]) => ({ itemId, rank }))
    .sort((a, b) => a.rank - b.rank);
}

/**
 * Check if a session was seeded from Catchball
 */
export function isSessionFromCatchball(session: PrioritizationSession): boolean {
  return session.seededFromCatchball === true && !!session.catchBallOutputId;
}

// ============================================================================
// Session Chat / Conversation Log
// ============================================================================

/**
 * A single message posted by a participant during a live session.
 * Persisted to `session_messages` in Supabase.
 */
export interface SessionMessage {
  id: string;
  sessionId: string;
  workspaceId: string;
  userId: string;
  /** Display name cached at write time; avoids a join on read */
  userName: string;
  body: string;
  createdAt: Date;
}
